import React, { FC, useContext, useEffect, useCallback, useState } from 'react';
import { Button } from '@clearcover/atlas';
import { useHistory } from 'react-router-dom';
import { AxiosRequestConfig } from 'axios';
import {
  AnimatedScreen,
  Footer,
  HRule,
  Paginate,
  Modal,
  ModalPromptContent,
  NoTableResults,
} from 'Shared/components';
import { useLazyBaseApi } from 'Shared/services/Api';
import { PartnerContext } from 'Admin/utils';
import {
  AttributionSearchType,
  AttributionPaginationType,
} from 'Admin/services/endpoints/PostAttributionSearch';

import { useStore } from '../../store';
import { buildPolicyListRow, Filters, PoliciesTable } from '../../components';

import styles from '../styles.module.css';

export const List: FC = () => {
  const { currentPartner } = useContext(PartnerContext);
  const history = useHistory();
  const [state, dispatch] = useStore();
  const [paginationPromptData, setPaginationPromptData] = useState<Partial<
    AttributionPaginationType
  > | null>(null);
  const closePaginationPromptModal = () => {
    setPaginationPromptData(null);
  };

  const [{ loading }, fetchPolicyData] = useLazyBaseApi<AttributionSearchType>({
    method: 'POST',
    url: 'policies/attribution/search',
  });

  const fetchPolicyAction = useCallback(
    async (config: AxiosRequestConfig) => {
      if (!loading) {
        dispatch({
          type: 'policies.loading',
          loading: true,
        });

        try {
          const policyDataResponse = await fetchPolicyData(config);

          if (policyDataResponse?.data) {
            dispatch({
              type: 'policies.success',
              data: policyDataResponse.data,
            });
          }
        } catch (err) {
          console.error(err);
        } finally {
          dispatch({
            type: 'policies.loading',
            loading: false,
          });
        }
      }
    },
    // checking for loading ensures existing requests don't get cancelled
    // loading dependency causes infinite loop of calls, so exclude it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, fetchPolicyData]
  );

  useEffect(() => {
    if (currentPartner?.id) {
      fetchPolicyAction({
        data: {
          filters: {
            attributedPartnerId: currentPartner?.id,
            attributedPortalUserId: state.from.agent?.id,
            policyEffectiveState: state.filters.states.length
              ? state.filters.states
              : undefined,
            policyNumber: state.filters.policyNumber
              ? state.filters.policyNumber
              : undefined,
          },
          pagination: {
            limit: state.policies.pagination.limit,
            offset: state.policies.pagination.offset,
          },
        },
      });
    }
  }, [
    currentPartner?.id,
    fetchPolicyAction,
    state.from.agent?.id,
    state.filters.states,
    state.filters.policyNumber,
    state.policies.pagination.limit,
    state.policies.pagination.offset,
  ]);

  if (!currentPartner) return null;

  return (
    <AnimatedScreen testId="partner-reattribution-page">
      <Modal
        showModal={Boolean(paginationPromptData)}
        onClose={closePaginationPromptModal}
      >
        <ModalPromptContent
          confirmButtonText="Continue without reattributing"
          cancelButtonText="Cancel"
          onCancel={closePaginationPromptModal}
          onConfirm={() => {
            // set pagination data to reducer
            dispatch({
              type: 'policies.pagination.update',
              pagination: {
                ...paginationPromptData,
              },
            });

            // clear selections
            dispatch({
              type: 'policies.selectAll',
              selected: false,
            });

            closePaginationPromptModal();
          }}
        >
          <h3>Continue without reattributing policies?</h3>
          <p>
            Your selections from this page will not be saved if you navigate to
            a new page. Are you sure you want to continue?
          </p>
        </ModalPromptContent>
      </Modal>
      <h1>Policy Reattribution</h1>
      <HRule size="lg" />
      <Filters />
      {(state.policies.loading || state.policies.data.length === 0) && (
        <NoTableResults loading={state.policies.loading} />
      )}
      {!state.policies.loading && state.policies.data.length > 0 && (
        <>
          <PoliciesTable
            hasPagination
            name="reattribution-list-table"
            rows={state.policies.data.map(row =>
              buildPolicyListRow(
                row,
                state.policies.selections.includes(row.universalPolicyId)
              )
            )}
            onCheckboxSelect={(key, selected) => {
              dispatch({
                type: 'policies.select',
                key: key.toString(),
                selected,
              });
            }}
            onCheckboxSelectAll={selected => {
              dispatch({
                type: 'policies.selectAll',
                selected,
              });
            }}
          />
          <Paginate
            total={state.policies.pagination.total}
            offset={state.policies.pagination.offset}
            limit={state.policies.pagination.limit}
            onOffsetChange={(limit, offset) => {
              // prompt if we are changing pagination but have selections made
              if (state.policies.selections.length > 0) {
                setPaginationPromptData({
                  limit,
                  offset,
                });
              } else {
                dispatch({
                  type: 'policies.pagination.update',
                  pagination: {
                    limit,
                    offset,
                  },
                });
              }
            }}
            onLimitChange={(limit: number) => {
              // prompt if we are changing pagination but have selections made
              if (state.policies.selections.length > 0) {
                setPaginationPromptData({
                  limit,
                });
              } else {
                dispatch({
                  type: 'policies.pagination.update',
                  pagination: {
                    limit,
                  },
                });
              }
            }}
          />
        </>
      )}
      <Footer hasSecondaryMenu={true}>
        <div className={styles.footerContainer}>
          <Button
            type="submit"
            data-testid="reattribution-list-submit"
            onClick={() => {
              history.push(
                `/admin/partners/${currentPartner.id}/reattribution/confirm`
              );
            }}
            disabled={!state.policies.selections.length}
          >
            Reattribute selected policies ({state.policies.selections.length})
          </Button>
        </div>
      </Footer>
    </AnimatedScreen>
  );
};
